import React, { useRef, useState } from 'react'
import {
  Button,
  Flex,
  H2,
  InputModal,
  Span,
  SelectDropdown,
  Line,
  EyeIcon,
  DownloadIcon,
  Card,
  P,
  Close,
  H4,
  Input,
  InputFile,
  CheckboxInput,
  PendingIcon,
  CheckCircleIcon,
  ReportIcon,
  ToolTip,
  StatusLabel,
  DateTimePicker,
} from '../../lib/components'
import './investment-detail.scss'
import { useBreakpoint } from '../../lib/utils/hooks'
import { XL } from '../../lib/utils/constants'
import {
  displayDollar,
  formatPercentage,
  formatStatus,
  getOrdinalSuffix,
  getTimestampWithTimezone,
  formatCents,
  formatCentsAsDecimalString,
  parseToCents,
  isPositiveDecimalAmount,
  getAllowedInvestmentStatusOptions,
  getCentsField,
  getReferenceValuation,
  previewUnderlyingExposurePercent,
} from '../../lib/utils/helpers'
import {
  InvestmentStatus,
  SOURCE_OF_FUNDS_LABELS,
  SOURCE_OF_INCOME_LABELS,
} from '../../lib/utils/message'
import { accountStatusMap, KycStatus } from '../../lib/types'
import { documentServices, kycServices } from '../../services'
import { CircularProgress } from '@mui/material'
import { InfoIcon } from '../../lib/components/Icon'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'

const InvestmentDetailPresenter = ({
  project,
  kycData,
  investorProfile,
  statuses,
  investorBankDetails,
  investmentDetail,
  handleFilePreview,
  handleFileUpload,
  handleStatus,
  isStatusUpdating = false,
  tradeExecutedAuditLogs = [],
  handleUpdateTradeExecutedDetails,
  allowInvestmentOrderStatusUpdates = true,
}: any) => {
  const { breakpoint } = useBreakpoint()
  const [displayInvestmentStatusModal, setDisplayInvestmentStatusModal] = useState(false)
  const [confirmationModal, setConfirmationModal] = useState(false)
  const [isAgreementLoading, setIsAgreementLoading] = useState(false)
  const [investmentStatusValue, setInvestmentStatusValue] = useState(investmentDetail.status)
  const [description, setDescription] = useState<string>('')
  const [tradeExecutionDate, setTradeExecutionDate] = useState<Date>(new Date())
  const [units, setUnits] = useState<string>('')
  const [actualInvestedAmountStr, setActualInvestedAmountStr] = useState<string>('')
  const [allotmentConfirmationKey, setAllotmentConfirmationKey] = useState<string>('')
  const [allotmentConfirmationFile, setAllotmentConfirmationFile] = useState<
    { name: string; type: string; size: number; date: Date } | undefined
  >(undefined)
  const [rateOfConversionStr, setRateOfConversionStr] = useState<string>('')
  const [skipEmail, setSkipEmail] = useState(false)
  const [error, setError] = useState<string>('')

  const referenceValuation = getReferenceValuation(investmentDetail, project)
  const opportunityCurrency = project?.currency_code ?? investmentDetail?.currency_code ?? undefined

  const getUnderlyingExposurePercent = (): number | undefined => {
    const raw =
      investmentDetail?.metadata?.underlyingExposurePercent ??
      investmentDetail?.metadata?.underlying_exposure_percent ??
      investmentDetail?.underlyingExposurePercent ??
      investmentDetail?.underlying_exposure_percent
    if (raw === null || raw === undefined || raw === '') return undefined
    const n = Number(raw)
    return Number.isFinite(n) ? n : undefined
  }

  const getDefaultActualInvestedAmountStr = () =>
    formatCentsAsDecimalString(investmentDetail?.amount)

  const getTradeExecutedUnitsError = (nextUnits = units): string => {
    if (!nextUnits || !String(nextUnits).trim()) return 'Units cannot be empty'
    const n = Number(nextUnits)
    if (!Number.isFinite(n) || n <= 0) return 'Enter a valid number of units'
    return ''
  }

  const getTradeExecutedAmountError = (nextInvestedAmount = actualInvestedAmountStr): string => {
    if (!isPositiveDecimalAmount(nextInvestedAmount)) return 'Please enter a valid amount'
    return ''
  }

  const getTradeExecutedRateError = (nextRate = rateOfConversionStr): string => {
    if (nextRate.trim()) {
      const rate = Number(nextRate)
      if (!Number.isFinite(rate) || rate <= 0) {
        return 'Rate of conversion must be greater than 0'
      }
    }
    return ''
  }

  const getTradeExecutedValidationError = (
    nextUnits = units,
    nextInvestedAmount = actualInvestedAmountStr,
    nextRate = rateOfConversionStr,
  ): string =>
    getTradeExecutedUnitsError(nextUnits) ||
    getTradeExecutedAmountError(nextInvestedAmount) ||
    getTradeExecutedRateError(nextRate)

  const getLiveUnderlyingExposurePreview = (): number | undefined => {
    if (!referenceValuation) return undefined
    const investedCents = isPositiveDecimalAmount(actualInvestedAmountStr)
      ? parseToCents(actualInvestedAmountStr)
      : Number(investmentDetail?.amount)
    const parsedRate = Number(rateOfConversionStr)
    const rate =
      rateOfConversionStr.trim() && Number.isFinite(parsedRate) && parsedRate > 0 ? parsedRate : 1
    return previewUnderlyingExposurePercent(investedCents, referenceValuation.amount, rate)
  }

  const resetAllotmentConfirmationUpload = () => {
    setAllotmentConfirmationKey('')
    setAllotmentConfirmationFile(undefined)
  }

  const getAllotmentConfirmation = (): { key?: string; downloadLink?: string } | undefined => {
    const raw =
      investmentDetail?.allotmentConfirmation ??
      investmentDetail?.allotment_confirmation ??
      investmentDetail?.metadata?.allotmentConfirmation ??
      investmentDetail?.metadata?.allotment_confirmation

    if (!raw) return undefined

    if (typeof raw === 'string' && raw.trim()) {
      return { key: raw }
    }

    if (typeof raw === 'object') {
      const key =
        typeof raw.key === 'string' ? raw.key : typeof raw.Key === 'string' ? raw.Key : undefined
      const downloadLink =
        typeof raw.downloadLink === 'string'
          ? raw.downloadLink
          : typeof raw.download_link === 'string'
          ? raw.download_link
          : undefined
      if ((key && key.trim()) || (downloadLink && downloadLink.trim())) {
        return { key, downloadLink }
      }
    }

    return undefined
  }

  const [displayTradeExecutedEditModal, setDisplayTradeExecutedEditModal] = useState(false)
  const [teTradeDate, setTeTradeDate] = useState<Date>(new Date())
  const [teAmountStr, setTeAmountStr] = useState('')
  const [teUnitsStr, setTeUnitsStr] = useState('')
  const [teRefStr, setTeRefStr] = useState('')
  const [teAllotmentConfirmationKey, setTeAllotmentConfirmationKey] = useState<string>('')
  const [teAllotmentConfirmationFile, setTeAllotmentConfirmationFile] = useState<
    { name: string; type: string; size: number; date: Date } | undefined
  >(undefined)
  const [teSkipEmail, setTeSkipEmail] = useState(false)
  const [teError, setTeError] = useState('')

  const resetTeAllotmentConfirmationUpload = () => {
    setTeAllotmentConfirmationKey('')
    setTeAllotmentConfirmationFile(undefined)
  }
  const initialTradeExecutedEditRef = useRef<{
    tradeExecutionDate: string
    amountCents: number
    quantity: number
    tradeReferenceDetails: string
  } | null>(null)

  const openTradeExecutedEditModal = () => {
    const inv = investmentDetail
    if (!inv?.id) return
    const execRaw =
      (inv.metadata && inv.metadata.tradeExecutionDate) ||
      inv.unit_allocation_date ||
      new Date().toISOString()
    const execDate = new Date(execRaw)
    const investedCents =
      getCentsField(inv, 'actualInvestedAmount', 'actual_invested_amount') ??
      getCentsField(inv?.metadata, 'actualInvestedAmount', 'actual_invested_amount') ??
      Number(inv.amount ?? 0)
    initialTradeExecutedEditRef.current = {
      tradeExecutionDate: Number.isNaN(execDate.getTime())
        ? new Date().toISOString()
        : execDate.toISOString(),
      amountCents: investedCents,
      quantity: Number(inv.quantity ?? 0),
      tradeReferenceDetails: (inv.metadata && inv.metadata.tradeReferenceDetails) || '',
    }
    setTeTradeDate(Number.isNaN(execDate.getTime()) ? new Date() : execDate)
    setTeAmountStr(formatCents(investedCents))
    setTeUnitsStr(inv.quantity !== null ? String(inv.quantity) : '')
    setTeRefStr((inv.metadata && inv.metadata.tradeReferenceDetails) || '')
    setTeError('')
    resetTeAllotmentConfirmationUpload()
    setTeSkipEmail(false)
    setDisplayTradeExecutedEditModal(true)
  }

  const submitTradeExecutedEdit = async () => {
    setTeError('')
    const init = initialTradeExecutedEditRef.current
    if (!init || !handleUpdateTradeExecutedDetails) return

    const nextIso = teTradeDate.toISOString()
    const initIso = new Date(init.tradeExecutionDate).toISOString()
    const payload: {
      tradeExecutionDate?: string
      amountCents?: number
      units?: number
      tradeReferenceDetails?: string
      allotmentConfirmation?: string
      skipEmail?: boolean
    } = {}
    if (nextIso !== initIso) {
      payload.tradeExecutionDate = nextIso
    }

    const nextCents = parseToCents(teAmountStr)
    if (nextCents <= 0) {
      setTeError('Enter a valid investment amount')
      return
    }
    if (nextCents !== init.amountCents) {
      payload.amountCents = nextCents
    }

    const nextUnits = Number(teUnitsStr)
    if (!Number.isFinite(nextUnits) || nextUnits <= 0) {
      setTeError('Enter a valid number of units')
      return
    }
    if (nextUnits !== init.quantity) {
      payload.units = nextUnits
    }

    if (teRefStr !== init.tradeReferenceDetails) {
      payload.tradeReferenceDetails = teRefStr
    }

    if (teAllotmentConfirmationKey) {
      payload.allotmentConfirmation = teAllotmentConfirmationKey
    }

    if (Object.keys(payload).length === 0) {
      setTeError('No changes to save')
      return
    }

    if (teSkipEmail) {
      payload.skipEmail = true
    }

    await handleUpdateTradeExecutedDetails(payload)
    resetTeAllotmentConfirmationUpload()
    setTeSkipEmail(false)
    setDisplayTradeExecutedEditModal(false)
  }

  const getTotalAmount = (...amounts: (string | number)[]): string => {
    return amounts.reduce((acc: number, b: string | number) => acc + Number(b ?? 0), 0).toString()
  }

  const getActualAmountReceived = (): number | undefined =>
    getCentsField(investmentDetail, 'actualAmountReceived', 'actual_amount_received') ??
    getCentsField(investmentDetail?.metadata, 'actualAmountReceived', 'actual_amount_received')

  const getActualInvestedAmount = (): number | undefined =>
    getCentsField(investmentDetail, 'actualInvestedAmount', 'actual_invested_amount') ??
    getCentsField(investmentDetail?.metadata, 'actualInvestedAmount', 'actual_invested_amount')

  // Share-priced orders (CB-239): shares, price and upfront fee are stored on the order (cents).
  const isShareOrder = Boolean(investmentDetail?.shares)
  const shareOrderCurrency = (investmentDetail && investmentDetail.currency_code) ?? 'USD'
  const shareOrderMoney = (cents: unknown) =>
    displayDollar(String(Number(cents ?? 0)), shareOrderCurrency, 2, true, true)

  const getTotalAmountBase = (): string | number =>
    (isShareOrder
      ? Number(getActualInvestedAmount() ?? investmentDetail?.amount ?? 0) +
        Number(investmentDetail?.sales_fee ?? 0)
      : undefined) ??
    getActualInvestedAmount() ??
    getActualAmountReceived() ??
    (investmentDetail && investmentDetail.amount) ??
    0

  const getKYCReportLink = async (investorKycId: string) => {
    const response = await kycServices.generateKYCReportLink(investorKycId)

    if (response.status === 200 && response.data) {
      return response.data
    }
  }

  const getDocumentDownloadLink = async (documentId: string) => {
    const response = await documentServices.getDocumentDownloadLink(documentId)
    if (response.status === 200 && response.data) {
      return response.data
    }
  }

  const handleGenerateKYCReportBtnClick = async (kycId: string) => {
    try {
      const { reportLink } = await getKYCReportLink(kycId)

      logCrmEvent(EngagementEvents.CLICKED_VIEW_KYC_REPORT, {
        page: 'investment-detail',
        investmentId: investmentDetail?.id,
        investorId: investorProfile?.userId,
      })

      window.open(reportLink)
    } catch (error) {
      console.error(error)
    }
  }

  const handleDownloadAgreementBtnClick = async (documentId: string) => {
    try {
      if (!documentId) return

      logCrmEvent(EngagementEvents.CLICKED_DOWNLOAD_CLIENT_AGREEMENT, {
        page: 'investment-detail',
        investmentId: investmentDetail?.id,
        investorId: investorProfile?.userId,
      })

      setIsAgreementLoading(true)
      const agreementLink = await getDocumentDownloadLink(documentId)
      setIsAgreementLoading(false)
      window.open(agreementLink)
    } catch (error) {
      console.error(error)
    }
  }

  const getMinTradeExecutionDate = () => {
    const d = new Date()
    d.setFullYear(d.getFullYear() - 20)
    return d
  }

  const getFormattedDate = (date: Date) => {
    const day = date.getDate()
    const month = date.toLocaleString('default', { month: 'short' })
    const year = date.getFullYear()
    const ordinalSuffix = getOrdinalSuffix(day)
    const formattedDate = `${day}${ordinalSuffix} ${month} ${year}`
    const formattedTime = getTimestampWithTimezone(date, 'HH:mm:ss')

    return { formattedDate, formattedTime }
  }

  return (
    <Flex variant='column' classes={['investment-detail-wrapper']}>
      {isStatusUpdating ? (
        <div className='loading-overlay' data-testid='investment-detail-status-updating-overlay'>
          <CircularProgress size={40} color='primary' className='loading-overlay__spinner' />
        </div>
      ) : null}
      <H2
        text='Investment Detail'
        classes={['align-center', 'investment-detail-wrapper-heading']}
      />

      {investorProfile && investorProfile?.accountStatus === accountStatusMap.DORMANT && (
        <div
          className='warning-container-fit-content  mb-24'
          data-testid='investment-detail-dormant-user-disclaimer'
        >
          <PendingIcon classes={['']} width='20px' height='20px' />
          This user is dormant
        </div>
      )}

      <Flex variant={breakpoint && breakpoint < XL ? 'column' : 'row'}>
        {project && project.opportunity_title && (
          <Card classes={['p-40', 'mb-48', 'mr-16']}>
            <P text='Opportunity Details' classes={['mb-16']} />
            <Flex variant='space-between'>
              <Span variant='bold' text='Opportunity Name' />
              <Span variant='thin' text={(project && project.opportunity_title) ?? ''} />
            </Flex>
            <Flex variant='space-between' classes={['mt-4']}>
              <Span variant='bold' text='Asset class' />
              <Span variant='thin' text={(project && project.asset_class) ?? ''} />
            </Flex>
            <Flex variant='space-between' classes={['mt-4']}>
              <Span variant='bold' text='Minimum Amount' />
              <Span
                variant='thin'
                text={displayDollar(
                  String((project && project.ticket_size) ?? 0),
                  project?.currency_code,
                )}
              />
            </Flex>
            <Flex variant='space-between' classes={['mt-4']}>
              <Span variant='bold' text='IRR' />
              <Span
                variant='thin'
                text={formatPercentage((project && project.investment_return) ?? 0)}
              />
            </Flex>
            <Flex variant='space-between' classes={['mt-4']}>
              <Span variant='bold' text='Raise Goal' />
              <Span
                variant='thin'
                text={displayDollar(
                  String((project && project.raise_goal) ?? 0),
                  project?.currency_code,
                )}
              />
            </Flex>
          </Card>
        )}

        {investorProfile && investorProfile.email && (
          <Card classes={['p-40', 'mb-48', 'mr-16']}>
            <P text='Client Details' classes={['mb-16']} />
            <Flex variant='space-between' classes={['mt-4']}>
              <Span variant='bold' text='Name' />
              <Span variant='thin' text={(investorProfile && investorProfile.name) ?? ''} />
            </Flex>
            <Flex variant='space-between' classes={['mt-4']}>
              <Span variant='bold' text='Email' />
              <Span variant='thin' text={(investorProfile && investorProfile.email) ?? ''} />
            </Flex>
            <Flex variant='space-between' classes={['mt-4']}>
              <Span variant='bold' text='Phone Number' />
              <Span variant='thin' text={(investorProfile && investorProfile.phoneNumber) ?? ''} />
            </Flex>
            <Flex variant='space-between' classes={['mt-4']}>
              <Span variant='bold' text='Country of Residence' />
              <Span
                variant='thin'
                text={(investorProfile && investorProfile.countryOfResidence) ?? ''}
              />
            </Flex>
            <Flex variant='space-between' classes={['mt-4']}>
              <Span variant='bold' text='Client ID' />
              <Span
                variant='thin'
                text={(investorProfile && investorProfile?.investorReference) ?? ''}
              />
            </Flex>
            <Flex variant='space-between' classes={['mt-4']}>
              <Span variant='bold' text='Portfolio ID' />
              <Span variant='thin' text={(kycData && kycData.investor_portfolio_id) ?? ''} />
            </Flex>
            {kycData && kycData?.metadata?.welcomeLetter && (
              <Flex
                variant='space-between'
                classes={['mt-4']}
                dataTestId='investment-detail-welcome-letter'
              >
                <Span variant='bold' text='Welcome Letter' />
                <EyeIcon
                  classes={['align-center', 'pointer', 'mt-4']}
                  height='24px'
                  width='24px'
                  onClick={async () => {
                    if (kycData && kycData?.metadata?.welcomeLetter) {
                      logCrmEvent(EngagementEvents.CLICKED_VIEW_WELCOME_LETTER, {
                        page: 'investment-detail',
                        investmentId: investmentDetail?.id,
                        investorId: investorProfile?.userId,
                      })
                      const url = kycData && kycData?.metadata?.welcomeLetter
                      url && window.open(url, '_blank')
                    }
                  }}
                  dataTestId='investment-detail-welcome-letter-view-icon'
                />
              </Flex>
            )}
          </Card>
        )}
      </Flex>

      <Flex variant={breakpoint && breakpoint < XL ? 'column' : 'row'}>
        {kycData && kycData.status && (
          <Card classes={['p-40', 'mb-48', 'mr-16']}>
            <P text='KYC Details' classes={['mb-16']} />
            <Flex variant='space-between'>
              <Span variant='bold' text='Status' />
              <Span variant='thin' text={(kycData && kycData.status) ?? ''} />
            </Flex>
            <Flex variant='space-between' classes={['mt-4']}>
              <Span variant='bold' text='Client Type' />
              <Span variant='thin' text={(investorProfile && investorProfile.role) ?? ''} />
            </Flex>
            {kycData && kycData?.id && (
              <Flex
                variant='space-between'
                classes={['mt-4']}
                dataTestId='investment-detail-kyc-report'
              >
                <Span variant='bold' text='KYC Report' />
                <EyeIcon
                  classes={['align-center', 'pointer', 'mt-4']}
                  height='24px'
                  width='24px'
                  dataTestId='investment-detail-kyc-report-view-icon'
                  onClick={() => handleGenerateKYCReportBtnClick(kycData?.id)}
                />
              </Flex>
            )}
          </Card>
        )}
        {investorBankDetails && (
          <Card classes={['p-40', 'mb-48', 'mr-16']}>
            <P text='Client Bank Details' classes={['mb-16']} />
            <Flex
              variant='space-between'
              dataTestId='investment-detail-investor-bank-details-beneficiary-name'
            >
              <Span variant='bold' text='Beneficiary Name' />
              <Span
                variant='thin'
                text={(investorBankDetails && investorBankDetails.beneficiaryName) ?? ''}
              />
            </Flex>
            <Flex
              variant='space-between'
              classes={['mt-4']}
              dataTestId='investment-detail-investor-bank-details-beneficiary-account-number'
            >
              <Span variant='bold' text='Beneficiary Account Number' />
              <Span
                variant='thin'
                text={(investorBankDetails && investorBankDetails.accountNumber) ?? ''}
              />
            </Flex>
            <Flex
              variant='space-between'
              classes={['mt-4']}
              dataTestId='investment-detail-investor-bank-details-beneficiary-bank-name'
            >
              <Span variant='bold' text={"Beneficiary's Bank Name"} />
              <Span
                variant='thin'
                text={(investorBankDetails && investorBankDetails.beneficiaryBank) ?? ''}
              />
            </Flex>
            <Flex
              variant='space-between'
              classes={['mt-4']}
              dataTestId='investment-detail-investor-bank-details-beneficiary-bank-address'
            >
              <Span variant='bold' text={"Beneficiary's Bank Address"} />

              {investorBankDetails && investorBankDetails?.bankAddress?.length > 25 ? (
                <ToolTip
                  content={investorBankDetails && investorBankDetails?.bankAddress}
                  contentClassNames={['tool-tip-container-fit']}
                >
                  <span>
                    <InfoIcon classes={['info-icon']} width='20px' height='24px' />
                  </span>
                </ToolTip>
              ) : (
                <Span
                  variant='thin'
                  classes={['right']}
                  text={(investorBankDetails && investorBankDetails.bankAddress) ?? ''}
                />
              )}
            </Flex>
            <Flex
              variant='space-between'
              classes={['mt-4']}
              dataTestId='investment-detail-investor-bank-details-swift-code'
            >
              <Span variant='bold' text={'SWIFT Code'} />
              <Span
                variant='thin'
                text={(investorBankDetails && investorBankDetails.swiftCode) ?? ''}
              />
            </Flex>
            <Flex
              variant='space-between'
              classes={['mt-4']}
              dataTestId='investment-detail-investor-bank-details-cif'
            >
              <Span variant='bold' text={'CIF Number'} />
              <Span variant='thin' text={(investorBankDetails && investorBankDetails.cif) ?? ''} />
            </Flex>
            <Flex
              variant='space-between'
              classes={['mt-4']}
              dataTestId='investment-detail-investor-bank-details-source-of-funds'
            >
              <Span variant='bold' text={'Source of Funds'} />

              {(() => {
                const sourceOfFunds: string[] = investorBankDetails?.sourceOfFunds ?? []
                const sourceOfFundsText = sourceOfFunds
                  .map((code: string) => SOURCE_OF_FUNDS_LABELS[code] ?? code)
                  .join(', ')

                return sourceOfFundsText.length > 25 ? (
                  <ToolTip
                    content={sourceOfFundsText}
                    contentClassNames={['tool-tip-container-fit']}
                  >
                    <span>
                      <InfoIcon classes={['info-icon']} width='20px' height='24px' />
                    </span>
                  </ToolTip>
                ) : (
                  <Span variant='thin' classes={['right']} text={sourceOfFundsText} />
                )
              })()}
            </Flex>
            <Flex
              variant='space-between'
              classes={['mt-4']}
              dataTestId='investment-detail-investor-bank-details-source-of-income'
            >
              <Span variant='bold' text={'Source of Income'} />

              {(() => {
                const sourceOfIncome: string[] = investorBankDetails?.sourceOfIncome ?? []
                const sourceOfIncomeText = sourceOfIncome
                  .map((code: string) => SOURCE_OF_INCOME_LABELS[code] ?? code)
                  .join(', ')

                return sourceOfIncomeText.length > 25 ? (
                  <ToolTip
                    content={sourceOfIncomeText}
                    contentClassNames={['tool-tip-container-fit']}
                  >
                    <span>
                      <InfoIcon classes={['info-icon']} width='20px' height='24px' />
                    </span>
                  </ToolTip>
                ) : (
                  <Span variant='thin' classes={['right']} text={sourceOfIncomeText} />
                )
              })()}
            </Flex>
            <Flex variant='space-between' classes={['mt-4']}>
              <Span variant='bold' text='Bank Details Confirmed' />

              {investmentDetail && investmentDetail?.metadata?.bankDetailsConfirmed ? (
                <CheckCircleIcon
                  classes={['mr-4']}
                  height='23px'
                  dataTestId='investment-detail-bank-details-confirmed-icon'
                />
              ) : (
                <ToolTip content={'Not Confirmed'} contentClassNames={['tool-tip-container-fit']}>
                  <span>
                    <ReportIcon
                      classes={['mr-4']}
                      height='23px'
                      dataTestId='investment-detail-bank-details-not-confirmed-icon'
                    />
                  </span>
                </ToolTip>
              )}
            </Flex>
          </Card>
        )}
      </Flex>

      <Flex variant={breakpoint && breakpoint < XL ? 'column' : 'row'}>
        {kycData && kycData.status && (
          <Card classes={['p-40', 'mb-48', 'mr-16']}>
            <P text='Overview of the status and meaning' classes={['mb-16']} />
            <div className='mt-12'>
              <Span variant='bold' text='Initiated:' />
              <Span
                variant='thin'
                text={
                  'Set by the system automatically – When the client has started the investment process.'
                }
              />
            </div>
            <div className='mt-12'>
              <Span variant='bold' text='Payment done:' />
              <Span
                variant='thin'
                text={
                  'Set by the system automatically – When the client has paid from the wallet. The order is placed in the next step; if it stays here, the client can place it from their dashboard or cancel for a refund.'
                }
              />
            </div>
            <div className='mt-12'>
              <Span variant='bold' text='Order placed:' />
              <Span
                variant='thin'
                text={
                  "Set by the system automatically – When all the steps from the client's side are completed, and the order is successfully placed."
                }
              />
            </div>
            <div className='mt-12'>
              <Span variant='bold' text='Rejected:' />
              <Span
                variant='thin'
                text={
                  'To be set by the admin manually – When the admin reviews and rejects the investment request.'
                }
              />
            </div>
            <div className='mt-12'>
              <Span variant='bold' text='Trade executed:' />
              <Span
                variant='thin'
                text={
                  'To be set by the admin manually – When the trade has been executed. The admin needs to input the units allocated to the client and the investment amount.'
                }
              />
            </div>
          </Card>
        )}

        {investmentDetail && (
          <Card classes={['p-40', 'mb-48', 'mr-16']}>
            <P text='Investment Details' classes={['mb-16']} />
            <Flex variant='space-between' dataTestId='investment-detail-order-id'>
              <Span variant='bold' text='Order ID' />
              <Span variant='thin' text={(investmentDetail && investmentDetail.order_id) ?? ''} />
            </Flex>
            <Flex
              variant='space-between'
              classes={['mt-4']}
              dataTestId='investment-detail-investment-status'
            >
              <Span variant='bold' text='Investment Status' />
              <Span
                variant='thin'
                text={formatStatus((investmentDetail && investmentDetail.status) ?? '')}
              />
            </Flex>

            {kycData && kycData?.agreement_document_id && (
              <Flex
                variant='space-between'
                classes={['mt-4']}
                dataTestId='investment-detail-client-agreement'
              >
                <Span variant='bold' text='Client Agreement' />

                {!isAgreementLoading ? (
                  <EyeIcon
                    classes={['align-center', 'pointer', 'mt-4']}
                    height='24px'
                    width='24px'
                    onClick={() => handleDownloadAgreementBtnClick(kycData?.agreement_document_id)}
                    dataTestId='investment-detail-client-agreement-view-icon'
                  />
                ) : (
                  <CircularProgress size={20} />
                )}
              </Flex>
            )}
            {/* Showing swift copy */}
            {investmentDetail && investmentDetail?.metadata?.paymentCopy && (
              <Flex
                variant='space-between'
                classes={['mt-4']}
                dataTestId='investment-detail-transaction-payment-copy'
              >
                <Span variant='bold' text='SWIFT Copy' />
                <EyeIcon
                  classes={['align-center', 'pointer', 'mt-4']}
                  height='24px'
                  width='24px'
                  dataTestId='investment-detail-transaction-payment-copy-view-icon'
                  onClick={async () => {
                    if (investmentDetail && investmentDetail?.metadata?.paymentCopy) {
                      logCrmEvent(EngagementEvents.CLICKED_VIEW_SWIFT_COPY, {
                        page: 'investment-detail',
                        investmentId: investmentDetail?.id,
                        investorId: investorProfile?.userId,
                      })
                      const url = await handleFilePreview(investmentDetail.metadata.paymentCopy)
                      url && window.open(url, '_blank')
                    }
                  }}
                />
              </Flex>
            )}
            {(() => {
              const allotmentConfirmation = getAllotmentConfirmation()
              if (!allotmentConfirmation) return null
              return (
                <Flex
                  variant='space-between'
                  classes={['mt-4']}
                  dataTestId='investment-detail-allotment-confirmation'
                >
                  <Span variant='bold' text='Allotment confirmation' />
                  <DownloadIcon
                    classes={['align-center', 'pointer', 'mt-4']}
                    height='16px'
                    width='16px'
                    dataTestId='investment-detail-allotment-confirmation-download-icon'
                    onClick={async () => {
                      if (allotmentConfirmation.downloadLink) {
                        window.open(allotmentConfirmation.downloadLink, '_blank')
                        return
                      }
                      if (!allotmentConfirmation.key) return
                      const url = await handleFilePreview(allotmentConfirmation.key)
                      url && window.open(url, '_blank')
                    }}
                  />
                </Flex>
              )
            })()}
            {/* Showing transaction number */}
            {investmentDetail &&
              !investmentDetail?.metadata?.paymentCopy &&
              investmentDetail?.metadata?.transactionNumber && (
                <Flex
                  variant='space-between'
                  classes={['mt-4']}
                  dataTestId='investment-detail-transaction-reference-number'
                >
                  <Span variant='bold' text='Transaction Reference Number' />
                  <Span
                    variant='thin'
                    text={(investmentDetail && investmentDetail?.metadata?.transactionNumber) ?? ''}
                  />
                </Flex>
              )}
            <Flex
              variant='space-between'
              classes={['mt-4']}
              dataTestId='investment-detail-investment-amount'
            >
              <Span variant='bold' text='Requested amount' />
              <Span
                variant='thin'
                text={displayDollar(
                  (investmentDetail && investmentDetail.amount) ?? 0,
                  (investmentDetail && investmentDetail.currency_code) ?? 'USD',
                  2,
                  true,
                  isShareOrder,
                )}
              />
            </Flex>
            {isShareOrder && (
              <>
                <Flex
                  variant='space-between'
                  classes={['mt-4']}
                  dataTestId='investment-detail-share-count'
                >
                  <Span variant='bold' text='Number of shares' />
                  <Span
                    variant='thin'
                    text={Number(investmentDetail.shares).toLocaleString('en-US')}
                  />
                </Flex>
                <Flex
                  variant='space-between'
                  classes={['mt-4']}
                  dataTestId='investment-detail-price-per-share'
                >
                  <Span variant='bold' text='Price per share' />
                  <Span variant='thin' text={shareOrderMoney(investmentDetail.price_per_share)} />
                </Flex>
                <Flex
                  variant='space-between'
                  classes={['mt-4']}
                  dataTestId='investment-detail-upfront-fees'
                >
                  <Span variant='bold' text='Upfront Fees' />
                  <Span variant='thin' text={shareOrderMoney(investmentDetail.sales_fee)} />
                </Flex>
                <Flex
                  variant='space-between'
                  classes={['mt-4']}
                  dataTestId='investment-detail-final-investment-amount'
                >
                  <Span variant='bold' text='Final Investment amount (incl. fees)' />
                  <Span variant='thin' text={shareOrderMoney(investmentDetail.final_amount)} />
                </Flex>
              </>
            )}
            {(() => {
              const actualAmountReceived = getActualAmountReceived()
              if (actualAmountReceived === undefined) return null
              return (
                <Flex
                  variant='space-between'
                  classes={['mt-4']}
                  dataTestId='investment-detail-actual-amount-received'
                >
                  <Span variant='bold' text='Actual amount received' />
                  <Span
                    variant='thin'
                    text={displayDollar(
                      actualAmountReceived.toString(),
                      (investmentDetail && investmentDetail.currency_code) ?? 'USD',
                      2,
                      true,
                      isShareOrder,
                    )}
                  />
                </Flex>
              )
            })()}
            {(() => {
              const actualInvestedAmount = getActualInvestedAmount()
              if (actualInvestedAmount === undefined) return null
              return (
                <Flex
                  variant='space-between'
                  classes={['mt-4']}
                  dataTestId='investment-detail-actual-invested-amount'
                >
                  <Span variant='bold' text='Actual invested amount' />
                  <Span
                    variant='thin'
                    text={displayDollar(
                      actualInvestedAmount.toString(),
                      (investmentDetail && investmentDetail.currency_code) ?? 'USD',
                      2,
                      true,
                      isShareOrder,
                    )}
                  />
                </Flex>
              )
            })()}
            {(() => {
              const underlyingExposurePercent = getUnderlyingExposurePercent()
              if (underlyingExposurePercent === undefined) return null
              return (
                <Flex
                  variant='space-between'
                  classes={['mt-4']}
                  dataTestId='investment-detail-underlying-exposure'
                >
                  <Span variant='bold' text='Underlying exposure' />
                  <Span variant='thin' text={formatPercentage(underlyingExposurePercent, 3)} />
                </Flex>
              )
            })()}
            <Flex
              variant='space-between'
              classes={['mt-4']}
              dataTestId='investment-detail-investment-platform-fee'
            >
              <Span variant='bold' text='Platform Fee' />
              <Span
                variant='thin'
                text={displayDollar(
                  // Todo, make platform_fee * 100 in client and remove it from here
                  (investmentDetail && investmentDetail?.platform_fee?.amount * 100) ?? 0,
                  (investmentDetail && investmentDetail?.platform_fee?.currency_code) ?? 'USD',
                  2,
                  true,
                )}
              />
            </Flex>
            <Flex
              variant='space-between'
              classes={['mt-4']}
              dataTestId='investment-detail-investment-total-amount'
            >
              <Span variant='bold' text='Total Amount' />
              <Span
                variant='thin'
                text={displayDollar(
                  getTotalAmount(
                    getTotalAmountBase(),
                    (investmentDetail && investmentDetail?.platform_fee?.amount * 100) ?? 0,
                  ),
                  (investmentDetail && investmentDetail?.platform_fee?.currency_code) ?? 'USD',
                  2,
                  true,
                  isShareOrder,
                )}
              />
            </Flex>
            {investmentDetail?.metadata?.investmentRefundDate && (
              <Flex
                variant='space-between'
                classes={['mt-4']}
                dataTestId='investment-detail-investment-refunded-amount'
              >
                <Span variant='bold' text='Refunded Amount' />
                <Span
                  variant='thin'
                  text={displayDollar(
                    (investmentDetail && investmentDetail?.metadata?.investmentRefundAmount) ?? 0,
                    (investmentDetail && investmentDetail?.platform_fee?.currency_code) ?? 'USD',
                    2,
                    true,
                  )}
                />
              </Flex>
            )}
            <Flex variant='space-between' classes={['mt-16']}>
              {displayInvestmentStatusModal ? (
                <InputModal>
                  <Flex
                    variant='column'
                    classes={['input-modal']}
                    dataTestId='investment-detail-investment-update-status-modal'
                  >
                    <Flex variant='space-between'>
                      <H4 text={'Update Investment Status'} />
                      <Close
                        height='24px'
                        onClick={() => {
                          if (isStatusUpdating) return
                          setDisplayInvestmentStatusModal(false)
                          setInvestmentStatusValue(investmentDetail.status)
                          setTradeExecutionDate(new Date())
                          setRateOfConversionStr('')
                          setSkipEmail(false)
                          resetAllotmentConfirmationUpload()
                        }}
                        dataTestId='investment-detail-investment-update-status-modal-close-button'
                      />
                    </Flex>
                    <Line />
                    <Flex variant='column' classes={['reply-section']}>
                      <Span variant='bold' text='Are you sure? You want to update.' />
                      <SelectDropdown
                        name='investmentStatus'
                        id='investmentStatusId'
                        formId=''
                        list={getAllowedInvestmentStatusOptions(investmentDetail?.status)}
                        value={investmentStatusValue || investmentDetail.status}
                        handleChange={(val) => {
                          const defaultInvestedAmount = getDefaultActualInvestedAmountStr()

                          setError(val === 'REJECTED' ? 'This field cannot be empty' : '')

                          if (val !== 'TRADE_EXECUTED') {
                            resetAllotmentConfirmationUpload()
                            setRateOfConversionStr('')
                            setActualInvestedAmountStr('')
                          } else {
                            setActualInvestedAmountStr(defaultInvestedAmount)
                            // Share-priced orders allot the ordered shares by default.
                            if (isShareOrder && !units) setUnits(String(investmentDetail.shares))
                          }

                          setInvestmentStatusValue(val)
                        }}
                        dataTestId='investment-detail-investment-update-status-select'
                      />

                      {investmentStatusValue && investmentStatusValue === 'REJECTED' && (
                        <>
                          <Input
                            labelText='Reason'
                            type='text'
                            name='reason'
                            placeholder='Enter reason for rejection'
                            value={description}
                            handleChange={(e) => {
                              if (e.target.value) setError('')
                              else setError('This field cannot be empty')
                              setDescription(e.target.value)
                            }}
                            required
                            dataTestId='investment-detail-investment-update-status-rejection-reason-input'
                          />
                          <StatusLabel
                            type='error'
                            variant='basic'
                            text={error}
                            dataTestId='investment-detail-rejected-reason-error'
                          />
                        </>
                      )}

                      {investmentStatusValue === 'TRADE_EXECUTED' && (
                        <>
                          <div className='input-label'>Trade execution date *</div>
                          <DateTimePicker
                            value={tradeExecutionDate ? new Date(tradeExecutionDate) : new Date()}
                            format='MMMM D, YYYY H:mm'
                            classes={['form-control', 'mt-8']}
                            minDate={getMinTradeExecutionDate()}
                            changeHandler={(date) => setTradeExecutionDate(date)}
                            dataTestId='investment-detail-trade-execution-datetime-picker'
                          />
                          <Input
                            labelText={isShareOrder ? 'Shares' : 'Units'}
                            type='number'
                            name='units'
                            placeholder={
                              isShareOrder
                                ? 'Enter no of shares to be allocated'
                                : 'Enter no of units to be allocated'
                            }
                            value={units}
                            classes={['no-spinner']}
                            handleChange={(e) => {
                              setUnits(e.target.value)
                              setError('')
                            }}
                            onWheel={(e: React.WheelEvent<HTMLInputElement>) => {
                              e.currentTarget.blur()
                            }}
                            required
                            dataTestId='investment-detail-trade-executed-units-input'
                          />
                          {getTradeExecutedUnitsError() ? (
                            <StatusLabel
                              type='error'
                              variant='basic'
                              text={getTradeExecutedUnitsError()}
                              dataTestId='investment-detail-trade-executed-units-error'
                            />
                          ) : null}
                          <Input
                            labelText='Investment amount'
                            type='text'
                            name='actualInvestedAmount'
                            placeholder='Amount'
                            value={actualInvestedAmountStr}
                            handleChange={(e) => {
                              setActualInvestedAmountStr(e.target.value)
                              setError('')
                            }}
                            required
                            dataTestId='investment-detail-trade-executed-investment-amount-input'
                          />
                          {getTradeExecutedAmountError() ? (
                            <StatusLabel
                              type='error'
                              variant='basic'
                              text={getTradeExecutedAmountError()}
                              dataTestId='investment-detail-trade-executed-amount-error'
                            />
                          ) : null}
                          <Input
                            labelText={
                              referenceValuation && opportunityCurrency
                                ? `Rate of conversion (${referenceValuation.currency} per 1 ${opportunityCurrency})`
                                : 'Rate of conversion'
                            }
                            type='number'
                            name='rateOfConversion'
                            placeholder='e.g. 3.6725'
                            value={rateOfConversionStr}
                            classes={['no-spinner']}
                            handleChange={(e) => {
                              setRateOfConversionStr(e.target.value)
                              setError('')
                            }}
                            onWheel={(e: React.WheelEvent<HTMLInputElement>) => {
                              e.currentTarget.blur()
                            }}
                            dataTestId='investment-detail-rate-of-conversion-input'
                          />
                          {getTradeExecutedRateError() ? (
                            <StatusLabel
                              type='error'
                              variant='basic'
                              text={getTradeExecutedRateError()}
                              dataTestId='investment-detail-trade-executed-rate-error'
                            />
                          ) : null}
                          <p className='investment-detail-field-hint'>
                            Optional. Valuation-currency units per 1 opportunity-currency unit (e.g.
                            AED per 1 USD = 3.6725).
                          </p>
                          {(() => {
                            const preview = getLiveUnderlyingExposurePreview()
                            if (preview === undefined) return null
                            return (
                              <Flex
                                variant='space-between'
                                classes={['mt-8']}
                                dataTestId='investment-detail-underlying-exposure-preview'
                              >
                                <Span variant='bold' text='Underlying exposure (preview)' />
                                <Span variant='thin' text={formatPercentage(preview, 3)} />
                              </Flex>
                            )
                          })()}
                          <div
                            className='allotment-confirmation-upload'
                            data-testid='investment-detail-allotment-confirmation-upload-wrapper'
                          >
                            <InputFile
                              name='allotmentConfirmation'
                              label='Allotment confirmation (optional)'
                              type='doc'
                              dataTestId='investment-detail-allotment-confirmation-upload'
                              file={allotmentConfirmationFile}
                              handleFiles={async (fileData) => {
                                const file = fileData as File | undefined
                                if (!handleFileUpload || !file) return
                                const data = await handleFileUpload(file, 'doc')
                                if (data?.data?.key) {
                                  setAllotmentConfirmationKey(data.data.key)
                                  setAllotmentConfirmationFile({
                                    name: file.name,
                                    type: file.type,
                                    size: file.size,
                                    date: new Date(),
                                  })
                                }
                              }}
                              handleDeleteFile={resetAllotmentConfirmationUpload}
                            />
                          </div>
                        </>
                      )}

                      <CheckboxInput
                        name='skipEmail'
                        labelText='Skip outgoing email'
                        value={skipEmail ? 'true' : 'false'}
                        handleChange={(e) => setSkipEmail(e.target.checked)}
                        dataTestId='investment-detail-skip-email-checkbox'
                      />

                      {confirmationModal ? (
                        <InputModal>
                          <Flex
                            variant='column'
                            classes={['input-modal']}
                            dataTestId='investment-detail-update-status-confirmation-modal'
                          >
                            <H4 text={'This action cannot be undone!'} />
                            <Line />
                            <Flex variant='column' classes={['reply-section']}>
                              <Span
                                variant='bold'
                                text={
                                  skipEmail
                                    ? 'Are you sure you want to update status without sending the status update email?'
                                    : 'Are you sure, you want to update status and send status update email?'
                                }
                              />

                              <Button
                                classes={['mt-24']}
                                variant='primary-btn'
                                text='Yes'
                                isDisabled={isStatusUpdating}
                                handleClick={async () => {
                                  if (isStatusUpdating) return

                                  const parsedRate = Number(rateOfConversionStr)
                                  await handleStatus({
                                    value: investmentStatusValue,
                                    field: 'investmentStatus',
                                    description: description.trim(),
                                    units,
                                    tradeExecutionDate,
                                    actualInvestedAmount:
                                      investmentStatusValue === 'TRADE_EXECUTED'
                                        ? actualInvestedAmountStr || undefined
                                        : undefined,
                                    allotmentConfirmation: allotmentConfirmationKey || undefined,
                                    rateOfConversion:
                                      investmentStatusValue === 'TRADE_EXECUTED' &&
                                      rateOfConversionStr.trim() &&
                                      Number.isFinite(parsedRate) &&
                                      parsedRate > 0
                                        ? parsedRate
                                        : undefined,
                                    skipEmail: skipEmail ? true : undefined,
                                  })

                                  setConfirmationModal(false)
                                  setDisplayInvestmentStatusModal(false)
                                  setRateOfConversionStr('')
                                  setActualInvestedAmountStr('')
                                  setSkipEmail(false)
                                  resetAllotmentConfirmationUpload()
                                }}
                              />

                              <Button
                                classes={['mt-24']}
                                variant='secondary-btn'
                                text='No'
                                isDisabled={isStatusUpdating}
                                handleClick={() => {
                                  if (isStatusUpdating) return
                                  setConfirmationModal(false)
                                  setDisplayInvestmentStatusModal(false)
                                  setRateOfConversionStr('')
                                  setActualInvestedAmountStr('')
                                  setSkipEmail(false)
                                  resetAllotmentConfirmationUpload()
                                }}
                              />
                            </Flex>
                          </Flex>
                        </InputModal>
                      ) : null}

                      <Button
                        classes={['mt-24']}
                        variant='primary-btn'
                        text='Update'
                        handleClick={() => {
                          if (investmentStatusValue === 'TRADE_EXECUTED') {
                            const validationError = getTradeExecutedValidationError()
                            if (validationError) {
                              setError(validationError)
                              return
                            }
                          }
                          setError('')
                          setConfirmationModal(true)
                        }}
                        dataTestId='investment-detail-update-investment-status-update-button'
                        isDisabled={
                          isStatusUpdating ||
                          (investmentStatusValue === 'TRADE_EXECUTED'
                            ? !!getTradeExecutedValidationError()
                            : !!error)
                        }
                      />
                    </Flex>
                  </Flex>
                </InputModal>
              ) : null}
              <Button
                variant='primary-btn'
                text='Update Investment Status'
                isDisabled={
                  isStatusUpdating ||
                  investmentDetail?.status === 'TRADE_EXECUTED' ||
                  investmentDetail?.status === 'REJECTED' ||
                  investmentDetail?.status === 'REFUND_PROCESSED' ||
                  !allowInvestmentOrderStatusUpdates
                }
                dataTestId='investment-detail-update-investment-status-button'
                handleClick={() => {
                  setRateOfConversionStr('')
                  setActualInvestedAmountStr('')
                  setSkipEmail(false)
                  setError('')
                  setDisplayInvestmentStatusModal(true)
                }}
              />
              {investmentDetail?.status === 'TRADE_EXECUTED' && handleUpdateTradeExecutedDetails ? (
                <Button
                  variant='secondary-btn'
                  text='Edit executed trade details'
                  classes={['ml-16']}
                  dataTestId='investment-detail-edit-trade-executed-button'
                  handleClick={openTradeExecutedEditModal}
                  isDisabled={!allowInvestmentOrderStatusUpdates}
                />
              ) : null}
            </Flex>
          </Card>
        )}
      </Flex>

      {displayTradeExecutedEditModal ? (
        <InputModal>
          <Flex
            variant='column'
            classes={['input-modal']}
            dataTestId='investment-detail-trade-executed-edit-modal'
          >
            <Flex variant='space-between'>
              <H4 text='Edit trade executed details' />
              <Close
                height='24px'
                onClick={() => setDisplayTradeExecutedEditModal(false)}
                dataTestId='investment-detail-trade-executed-edit-modal-close'
              />
            </Flex>
            <Line />
            <P
              text='Correct trade execution date, invested amount, allocated units, or reference details. Changes sync to holdings and transaction history where applicable.'
              classes={['mb-16']}
            />
            <div className='input-label'>Trade execution date *</div>
            <DateTimePicker
              value={teTradeDate}
              format='MMMM D, YYYY H:mm'
              classes={['form-control', 'mt-8', 'mb-16']}
              changeHandler={(date) => setTeTradeDate(date)}
            />
            <Input
              labelText='Investment amount (major units, e.g. dollars)'
              type='text'
              name='teAmount'
              placeholder='Amount'
              value={teAmountStr}
              handleChange={(e) => setTeAmountStr(e.target.value)}
              required
              dataTestId='investment-detail-trade-executed-edit-amount'
            />
            <Input
              labelText='Allocated units'
              type='text'
              name='teUnits'
              placeholder='Units'
              value={teUnitsStr}
              handleChange={(e) => setTeUnitsStr(e.target.value)}
              required
              dataTestId='investment-detail-trade-executed-edit-units'
            />
            <div className='input-label mt-16'>Trade / wire reference (optional)</div>
            <textarea
              className='form-control mt-8'
              rows={3}
              value={teRefStr}
              onChange={(e) => setTeRefStr(e.target.value)}
              data-testid='investment-detail-trade-executed-edit-reference'
            />
            <div
              className='allotment-confirmation-upload mt-16'
              data-testid='investment-detail-te-edit-allotment-confirmation-upload-wrapper'
            >
              <InputFile
                name='teAllotmentConfirmation'
                label='Replace allotment confirmation (optional)'
                type='doc'
                dataTestId='investment-detail-te-edit-allotment-confirmation-upload'
                file={teAllotmentConfirmationFile}
                handleFiles={async (fileData) => {
                  const file = fileData as File | undefined
                  if (!handleFileUpload || !file) return
                  const data = await handleFileUpload(file, 'doc')
                  if (data?.data?.key) {
                    setTeAllotmentConfirmationKey(data.data.key)
                    setTeAllotmentConfirmationFile({
                      name: file.name,
                      type: file.type,
                      size: file.size,
                      date: new Date(),
                    })
                  }
                }}
                handleDeleteFile={resetTeAllotmentConfirmationUpload}
              />
            </div>
            <P
              text='Leave this empty to regenerate the confirmation letter from the corrected details above.'
              classes={['mt-4', 'mb-8']}
            />
            <CheckboxInput
              name='teSkipEmail'
              labelText='Skip outgoing email'
              value={teSkipEmail ? 'true' : 'false'}
              handleChange={(e) => setTeSkipEmail(e.target.checked)}
              dataTestId='investment-detail-te-edit-skip-email-checkbox'
            />
            {teError ? (
              <StatusLabel
                type='error'
                variant='basic'
                text={teError}
                dataTestId='investment-detail-te-edit-error'
              />
            ) : null}
            <Button
              classes={['mt-24']}
              variant='primary-btn'
              text='Save changes'
              dataTestId='investment-detail-trade-executed-edit-save'
              handleClick={() => void submitTradeExecutedEdit()}
            />
          </Flex>
        </InputModal>
      ) : null}

      <Flex variant={breakpoint && breakpoint < XL ? 'column' : 'row'}>
        <Card classes={['p-40', 'mb-48', 'mr-16']}>
          <P text='Client Important checklist' classes={['mb-16']} />
          <Flex variant='space-between'>
            <Span variant='bold' text='Email Verified' />

            {investorProfile && investorProfile.isEmailVerified ? (
              <CheckCircleIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='investment-detail-email-verified-icon'
              />
            ) : (
              <ReportIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='investment-detail-email-unverified-icon'
              />
            )}
          </Flex>

          <Flex variant='space-between' classes={['mt-4']}>
            <Span variant='bold' text='KYC Complete' />

            {kycData && kycData.status === KycStatus.APPROVED ? (
              <CheckCircleIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='investment-detail-kyc-complete-icon'
              />
            ) : (
              <ToolTip
                content={kycData && kycData.status}
                contentClassNames={['tool-tip-container-fit']}
              >
                <span>
                  <ReportIcon
                    classes={['mr-4']}
                    height='23px'
                    dataTestId='investment-detail-kyc-incomplete-icon'
                  />
                </span>
              </ToolTip>
            )}
          </Flex>

          <Flex variant='space-between' classes={['mt-4']}>
            <Span variant='bold' text='Confidentiality Agreement Accepted' />

            {kycData && kycData.is_nda_accepted ? (
              <CheckCircleIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='investment-detail-nda-accepted-icon'
              />
            ) : (
              <ReportIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='investment-detail-nda-not-accepted-icon'
              />
            )}
          </Flex>

          <Flex variant='space-between' classes={['mt-4']}>
            <Span variant='bold' text='Risk Checklist Accepted' />

            {kycData && kycData.investment_essentials_agreement ? (
              <CheckCircleIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='investment-detail-risk-checklist-accepted-icon'
              />
            ) : (
              <ReportIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='investment-detail-risk-checklist-not-accepted-icon'
              />
            )}
          </Flex>

          <Flex variant='space-between' classes={['mt-4']}>
            <Span variant='bold' text='Terms of Business Accepted' />

            {kycData && kycData.accept_tob ? (
              <CheckCircleIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='investment-detail-terms-of-business-accepted-icon'
              />
            ) : (
              <ReportIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='investment-detail-terms-of-business-not-accepted-icon'
              />
            )}
          </Flex>

          <Flex variant='space-between' classes={['mt-4']}>
            <Span variant='bold' text='Transaction Checklist Accepted' />

            {kycData && kycData.transaction_checklist_agreement ? (
              <CheckCircleIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='investment-detail-transaction-checklist-accepted-icon'
              />
            ) : (
              <ReportIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='investment-detail-transaction-checklist-not-accepted-icon'
              />
            )}
          </Flex>

          <Flex variant='space-between' classes={['mt-4']}>
            <Span variant='bold' text='Client Agreement Signed' />

            {kycData && kycData.agreement_document_id ? (
              <CheckCircleIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='investment-detail-agreement-signed-icon'
              />
            ) : (
              <ReportIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='investment-detail-agreement-not-signed-icon'
              />
            )}
          </Flex>

          <Flex variant='space-between' classes={['mt-4']}>
            <Span variant='bold' text='Welcome Letter Shared' />

            {kycData && kycData?.metadata?.welcomeLetter ? (
              <CheckCircleIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='investment-detail-welcome-letter-shared-icon'
              />
            ) : (
              <ReportIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='investment-detail-welcome-letter-not-shared-icon'
              />
            )}
          </Flex>
          <Flex variant='space-between' classes={['mt-4']}>
            <Span variant='bold' text='Self Declaration Agreement' />

            {kycData && kycData?.self_declaration ? (
              <CheckCircleIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='investment-detail-self-declaration-agreement-signed-icon'
              />
            ) : (
              <ReportIcon
                classes={['mr-4']}
                height='23px'
                dataTestId='investment-detail-self-declaration-agreement-not-signed-icon'
              />
            )}
          </Flex>
        </Card>

        <Card classes={['p-40', 'mb-48', 'mr-16']}>
          <P text='Details of investment with date and time' classes={['mb-16']} />
          {investmentDetail.metadata?.investmentInitiatedDate && (
            <Flex variant='space-between' dataTestId='investment-detail-investment-initiation-date'>
              <Span variant='bold' text='Investment initiated on' />
              <Span
                variant='thin'
                text={`${
                  getFormattedDate(new Date(investmentDetail.metadata.investmentInitiatedDate))
                    .formattedDate
                } - 
                  ${
                    getFormattedDate(new Date(investmentDetail.metadata.investmentInitiatedDate))
                      .formattedTime
                  }
                  `}
              />
            </Flex>
          )}

          {investmentDetail.metadata?.investmentOrderPlacedDate && (
            <Flex
              variant='space-between'
              dataTestId='investment-detail-investment-order-placed-date'
            >
              <Span variant='bold' text='Investment order processed on' />
              <Span
                variant='thin'
                text={`${
                  getFormattedDate(new Date(investmentDetail.metadata.investmentOrderPlacedDate))
                    .formattedDate
                } - 
                  ${
                    getFormattedDate(new Date(investmentDetail.metadata.investmentOrderPlacedDate))
                      .formattedTime
                  }
                  `}
              />
            </Flex>
          )}

          {investmentDetail.metadata?.paymentReceivedDate && (
            <Flex
              variant='space-between'
              dataTestId='investment-detail-investment-payment-received-date'
            >
              <Span variant='bold' text='Payment received on' />
              <Span
                variant='thin'
                text={`${
                  getFormattedDate(new Date(investmentDetail.metadata.paymentReceivedDate))
                    .formattedDate
                } - 
                  ${
                    getFormattedDate(new Date(investmentDetail.metadata.paymentReceivedDate))
                      .formattedTime
                  }
                  `}
              />
            </Flex>
          )}

          {investmentDetail.metadata?.investmentApprovalDate && (
            <Flex variant='space-between' dataTestId='investment-detail-investment-approval-date'>
              <Span variant='bold' text='Investment approved on' />
              <Span
                variant='thin'
                text={`${
                  getFormattedDate(new Date(investmentDetail.metadata.investmentApprovalDate))
                    .formattedDate
                } - 
                  ${
                    getFormattedDate(new Date(investmentDetail.metadata.investmentApprovalDate))
                      .formattedTime
                  }
                  `}
              />
            </Flex>
          )}

          {investmentDetail.metadata?.tradeExecutionDate && (
            <Flex variant='space-between' dataTestId='investment-detail-trade-execution-date'>
              <Span variant='bold' text='Trade executed on' />
              <Span
                variant='thin'
                text={`${
                  getFormattedDate(new Date(investmentDetail.metadata.tradeExecutionDate))
                    .formattedDate
                } - 
                  ${
                    getFormattedDate(new Date(investmentDetail.metadata.tradeExecutionDate))
                      .formattedTime
                  }
                  `}
              />
            </Flex>
          )}
        </Card>
      </Flex>

      <Flex variant='column' classes={['table_scroll_horizontal']}>
        {investmentDetail?.status === 'TRADE_EXECUTED' &&
        tradeExecutedAuditLogs &&
        tradeExecutedAuditLogs.length > 0 ? (
          <>
            <H4 text='Investment detail audit (trade executed corrections)' classes={['mb-16']} />
            <table className='styled-table mb-48'>
              <thead>
                <tr>
                  <th>When (UTC)</th>
                  <th>Admin user id</th>
                  <th>Event</th>
                  <th>Summary</th>
                </tr>
              </thead>
              <tbody>
                {tradeExecutedAuditLogs.map((row: Record<string, unknown>) => {
                  const meta = row.metadata as
                    | { before?: Record<string, unknown>; after?: Record<string, unknown> }
                    | undefined
                  const summary =
                    meta && meta.before && meta.after
                      ? JSON.stringify({ before: meta.before, after: meta.after })
                      : JSON.stringify(meta ?? {})
                  return (
                    <tr key={String(row.id ?? row.createdAt ?? summary.slice(0, 40))}>
                      <td>
                        {row.createdAt
                          ? `${new Date(String(row.createdAt))
                              .toISOString()
                              .replace('T', ' ')
                              .slice(0, 19)} UTC`
                          : '—'}
                      </td>
                      <td>{String(row.who ?? '—')}</td>
                      <td>{String(row.eventType ?? '—')}</td>
                      <td style={{ maxWidth: 480, wordBreak: 'break-word' as const }}>{summary}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </>
        ) : null}
        {statuses && statuses.length > 0 && (
          <>
            <H4 text='Status logs' />
          </>
        )}
        {statuses && statuses.length > 0 && (
          <table className='styled-table'>
            <thead>
              <tr>
                <th>Updated By</th>
                <th>Updated At</th>
                <th>Investment Status</th>
                <th>Payment Status</th>
              </tr>
            </thead>
            <tbody>
              {statuses
                .sort((a: any, b: any) => (a.updatedAt < b.updatedAt ? 1 : -1))
                .map((doc: any) => {
                  return (
                    !!doc.id && (
                      <tr key={doc.updatedAt}>
                        <td>{doc.email}</td>
                        <td>{`${new Date(doc.updatedAt).toLocaleString()}`}</td>
                        <td>{formatStatus(doc.investmentStatus)}</td>
                        <td>{formatStatus(doc.paymentStatus)}</td>
                      </tr>
                    )
                  )
                })}
            </tbody>
          </table>
        )}
      </Flex>
    </Flex>
  )
}

export { InvestmentDetailPresenter }
